import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

const A4_WIDTH_MM = 210
const A4_HEIGHT_MM = 297
const CAPTURE_SCALE = 1.5
const JPEG_QUALITY = 0.85
const BACKGROUND_MIN_CHANNEL = 250
const SAMPLE_STEP_PX = 4
const CUT_SEARCH_RATIO = 0.35
const ORPHAN_HEADING_RATIO = 0.12

type Slice = { top: number; height: number }

const isBackgroundRow = (row: Uint8ClampedArray, rowStart: number, width: number): boolean => {
    for (let x = 0; x < width; x += SAMPLE_STEP_PX) {
        const i = rowStart + x * 4
        if (
            row[i] < BACKGROUND_MIN_CHANNEL ||
            row[i + 1] < BACKGROUND_MIN_CHANNEL ||
            row[i + 2] < BACKGROUND_MIN_CHANNEL
        ) {
            return false
        }
    }
    return true
}

const findCutY = (
    context: CanvasRenderingContext2D,
    width: number,
    idealY: number,
    searchHeight: number
): number => {
    const startY = Math.max(0, idealY - searchHeight)
    const height = idealY - startY
    if (height <= 0) return idealY

    const { data } = context.getImageData(0, startY, width, height)
    for (let y = height - 1; y >= 0; y--) {
        if (isBackgroundRow(data, y * width * 4, width)) {
            return startY + y
        }
    }
    return idealY
}

type Block = { top: number; bottom: number }

const collectRects = (element: HTMLElement, canvasHeight: number, selector: string): Block[] => {
    const rootTop = element.getBoundingClientRect().top
    const ratio = canvasHeight / element.scrollHeight
    if (!isFinite(ratio) || ratio <= 0) return []

    return Array.from(element.querySelectorAll<HTMLElement>(selector))
        .map((node) => {
            const rect = node.getBoundingClientRect()
            return {
                top: Math.floor((rect.top - rootTop) * ratio),
                bottom: Math.ceil((rect.bottom - rootTop) * ratio)
            }
        })
        .filter((block) => block.bottom > block.top)
        .sort((a, b) => a.top - b.top)
}

const blockContaining = (blocks: Block[], y: number): Block | undefined =>
    blocks.find((block) => y > block.top && y < block.bottom)

const avoidOrphanHeading = (
    headings: Block[],
    cutY: number,
    pageTop: number,
    pageHeightPx: number
): number => {
    const maxOrphanGap = Math.floor(pageHeightPx * ORPHAN_HEADING_RATIO)
    const orphan = headings.find(
        (heading) =>
            heading.bottom <= cutY && cutY - heading.bottom <= maxOrphanGap && heading.top > pageTop
    )
    return orphan ? orphan.top : cutY
}

const buildSlices = (
    canvas: HTMLCanvasElement,
    pageHeightPx: number,
    blocks: Block[],
    headings: Block[]
): Slice[] => {
    const context = canvas.getContext('2d')
    const slices: Slice[] = []
    const searchHeight = Math.floor(pageHeightPx * CUT_SEARCH_RATIO)
    let top = 0

    while (top < canvas.height) {
        const remaining = canvas.height - top
        if (remaining <= pageHeightPx) {
            slices.push({ top, height: remaining })
            break
        }

        const idealY = top + pageHeightPx
        const blocked = blockContaining(blocks, idealY)
        let cutY: number

        if (blocked && blocked.top > top) {
            cutY = blocked.top
        } else {
            cutY = context ? findCutY(context, canvas.width, idealY, searchHeight) : idealY
        }

        cutY = avoidOrphanHeading(headings, cutY, top, pageHeightPx)

        const height = cutY > top ? cutY - top : pageHeightPx
        slices.push({ top, height })
        top += height
    }

    return slices
}

const FONT_METRICS_FIX_CSS =
    'div[style*="visibility: hidden"] img { display: inline !important; vertical-align: baseline !important; }'

const withFontMetricsFix = async <T>(run: () => Promise<T>): Promise<T> => {
    const style = document.createElement('style')
    style.textContent = FONT_METRICS_FIX_CSS
    document.head.appendChild(style)
    try {
        return await run()
    } finally {
        style.remove()
    }
}

export const exportElementToPdf = async (element: HTMLElement, fileName: string): Promise<void> => {
    const canvas = await withFontMetricsFix(() =>
        html2canvas(element, {
            scale: CAPTURE_SCALE,
            useCORS: true,
            logging: false,
            windowWidth: 1200,
            backgroundColor: '#ffffff'
        })
    )

    const pxPerMm = canvas.width / A4_WIDTH_MM
    const pageHeightPx = Math.floor(A4_HEIGHT_MM * pxPerMm)
    const blocks = collectRects(element, canvas.height, '[data-pdf-block]')
    const headings = collectRects(element, canvas.height, '[data-pdf-heading]')
    const slices = buildSlices(canvas, pageHeightPx, blocks, headings)

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })

    slices.forEach((slice, index) => {
        if (index > 0) pdf.addPage()

        const pageCanvas = document.createElement('canvas')
        pageCanvas.width = canvas.width
        pageCanvas.height = slice.height
        const pageContext = pageCanvas.getContext('2d')
        if (!pageContext) return

        pageContext.fillStyle = '#ffffff'
        pageContext.fillRect(0, 0, pageCanvas.width, pageCanvas.height)
        pageContext.drawImage(
            canvas,
            0,
            slice.top,
            canvas.width,
            slice.height,
            0,
            0,
            canvas.width,
            slice.height
        )

        pdf.addImage(
            pageCanvas.toDataURL('image/jpeg', JPEG_QUALITY),
            'JPEG',
            0,
            0,
            A4_WIDTH_MM,
            slice.height / pxPerMm
        )
    })

    pdf.save(fileName)
}
